import React, { useState } from 'react';
import { Box, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import CreateInvoice from './CreateInvoice';
import IvyInvoice from './IvyInvoice';
import { useT } from '../i18n/LanguageContext';

/**
 * Invoicing — one entry point for both billing documents.
 *
 * <p>These were two screens, Create Invoice and Settlements, and the split was never about the
 * filters: it was about the fact that they produce <b>two genuinely different documents</b>.
 *
 * <ul>
 *   <li><b>Zone-rate invoice</b> — tickets grouped by customer and FS code, priced at a zone rate.
 *       What Meta bills a customer.</li>
 *   <li><b>Settlement sheet</b> — one row per ticket on a dispatcher's own printed form (Bobcat, LSJ,
 *       Melendrez…), with the gross / commission / rent / fuel footer. What Meta submits to a
 *       dispatcher to get paid.</li>
 * </ul>
 *
 * <p>So this combines the <i>entry point</i> and leaves each document's own filters and output
 * exactly as they were. Folding them into one form would have meant forcing the zone-rate grouping
 * into the settlement data model — its Zone Rate line has no per-ticket equivalent — and the first
 * thing to break would have been a live invoice. One screen, two modes, neither one compromised.
 */

const ZONE = 'ZONE';
const SETTLEMENT = 'SETTLEMENT';

export default function Invoicing() {
  // Settlements is the more frequently used of the two, so it opens there.
  const [mode, setMode] = useState(SETTLEMENT);
  const t = useT();

  return (
    <Box>
      <Paper sx={{ px: { xs: 2, md: 3 }, pt: 2.5, pb: 2, mb: 1 }} elevation={0}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Typography variant="h5" fontWeight={700}>{t('inv.title')}</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(e, v) => v !== null && setMode(v)}
            aria-label={t('inv.docType')}
            sx={{ width: { xs: '100%', sm: 'auto' }, '& .MuiToggleButton-root': { flex: { xs: 1, sm: 'none' }, minHeight: { xs: 44, md: 'auto' }, textTransform: 'none' } }}
          >
            <ToggleButton value={SETTLEMENT}>
              <DescriptionIcon fontSize="small" sx={{ mr: 0.75 }} />
              {t('inv.settlement')}
            </ToggleButton>
            <ToggleButton value={ZONE}>
              <ReceiptIcon fontSize="small" sx={{ mr: 0.75 }} />
              {t('inv.zone')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {mode === SETTLEMENT ? t('inv.settlementHelp') : t('inv.zoneHelp')}
        </Typography>
      </Paper>

      {/* Both are mounted lazily by mode rather than hidden with CSS: each fetches on mount, and
          keeping the other alive would fire a second set of invoice queries nobody asked for. */}
      {mode === SETTLEMENT ? <IvyInvoice /> : <CreateInvoice />}
    </Box>
  );
}
